import type { MessageThread } from "../../core/types";
import storageService from "./storageService";
import { detectLocale, getTimeZone } from "../../utils/localeUtil";
import { getCurrentSanitizedUrl } from "../../utils/pageUrlUtil";
import { getErrorStatus } from "../utils/getErrorStatus";

export type MessageThreadResult =
  | { success: true; messageThread: MessageThread }
  | { success: false; status: number | undefined };

export type MessageThreadCollectionResult =
  | { success: true; messageThreads: MessageThread[] }
  | { success: false; status: number | undefined };

export const getLinkTrackingUrl = (
  apiUrl: string,
  token: string,
  widgetId: string,
  messageThreadId: string,
  url: string,
): string =>
  `${apiUrl}/widgets/` +
  `${widgetId}/message_threads/` +
  `${messageThreadId}/click?` +
  `session_id=${storageService.getSessionId()}&` +
  `uuid=${storageService.getContact(widgetId, "uuid") as string}&` +
  `token=${token}&` +
  `url=${encodeURIComponent(url)}`;

export const getMessageThreads = async (
  apiUrl: string,
  token: string,
  widgetId: string,
  signal: AbortSignal,
): Promise<MessageThreadCollectionResult> => {
  const url = new URL(`${apiUrl}/widgets/${widgetId}/message_threads`);
  url.searchParams.set(
    "uuid",
    storageService.getContact(widgetId, "uuid") as string,
  );

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`, {
        cause: response.status,
      });
    }

    const messageThreads = (await response.json()) as MessageThread[];
    return { success: true, messageThreads };
  } catch (error) {
    return { success: false, status: getErrorStatus(error) };
  }
};

export const getMessageThread = async (
  apiUrl: string,
  token: string,
  widgetId: string,
  messageThreadId: string,
  signal: AbortSignal,
): Promise<MessageThreadResult> => {
  const url = new URL(
    `${apiUrl}/widgets/${widgetId}/message_threads/${messageThreadId}`,
  );
  url.searchParams.set("session_id", storageService.getSessionId());
  url.searchParams.set(
    "uuid",
    storageService.getContact(widgetId, "uuid") as string,
  );

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`, {
        cause: response.status,
      });
    }

    const messageThread = (await response.json()) as MessageThread;
    return { success: true, messageThread };
  } catch (error) {
    return { success: false, status: getErrorStatus(error) };
  }
};

export const createMessageThread = async (
  apiUrl: string,
  token: string,
  widgetId: string,
  userEmail: string,
  userInput: string,
  attachmentId: string | null,
  signal: AbortSignal,
  homeUrl?: string,
  skipIngest?: boolean,
  hubIdentity?: string,
): Promise<MessageThreadResult> => {
  const url = new URL(`${apiUrl}/widgets/${widgetId}/message_threads`);
  url.searchParams.set("lang", detectLocale() ?? "");
  const visitorTimeZone = getTimeZone();

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...storageService.getUtmParams(),
        attachmentId,
        contents: userInput,
        session_id: storageService.getSessionId(),
        uuid: storageService.getContact(widgetId, "uuid"),
        email: userEmail,
        homeUrl,
        current_url: getCurrentSanitizedUrl(),
        skip_ingest: skipIngest,
        ...(visitorTimeZone ? { visitor_time_zone: visitorTimeZone } : {}),
        ...(hubIdentity ? { hub_identity: hubIdentity } : {}),
      }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`, {
        cause: response.status,
      });
    }

    const messageThread = (await response.json()) as MessageThread;
    return { success: true, messageThread };
  } catch (error) {
    return { success: false, status: getErrorStatus(error) };
  }
};

